import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Plus, Trash2, Save, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const adminHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("bnm_admin_token")}` });

const fieldCls = "w-full rounded-md border border-white/15 bg-white/5 px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-volt focus:outline-none";
const cardCls = "rounded-lg border border-white/10 bg-white/5 p-5";

function ListEditor({ title, hint, items, onChange, testid }) {
  return (
    <div className={cardCls}>
      <h3 className="font-display text-sm font-bold text-white">{title}</h3>
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
      <div className="mt-3 space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={it}
              onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))}
              className={fieldCls}
              data-testid={`${testid}-item-${i}`}
            />
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}
              data-testid={`${testid}-remove-${i}`} aria-label="Remove item"
              className="rounded-md border border-white/15 p-2 text-slate-400 hover:text-red-400 hover:border-red-400/40 transition-colors">
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...items, ""])} data-testid={`${testid}-add`}
        className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-2 text-xs font-semibold text-slate-300 hover:text-volt hover:border-volt/40 transition-colors">
        <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add item
      </button>
    </div>
  );
}

export default function AdminChat() {
  const [kb, setKb] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [openSession, setOpenSession] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [k, s] = await Promise.all([
        axios.get(`${API}/admin/chat/knowledge`, { headers: adminHeaders() }),
        axios.get(`${API}/admin/chat/sessions`, { headers: adminHeaders() }),
      ]);
      setKb(k.data);
      setSessions(s.data.sessions || []);
    } catch {
      toast.error("Could not load AI assistant data");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/chat/knowledge`, kb, { headers: adminHeaders() });
      toast.success("AI knowledge updated — the assistant uses it immediately");
    } catch {
      toast.error("Could not save knowledge");
    } finally {
      setSaving(false);
    }
  };

  const viewSession = async (sid) => {
    if (openSession === sid) {
      setOpenSession(null);
      setTranscript(null);
      return;
    }
    setOpenSession(sid);
    try {
      const { data } = await axios.get(`${API}/admin/chat/sessions/${sid}`, { headers: adminHeaders() });
      setTranscript(data);
    } catch {
      toast.error("Could not load conversation");
    }
  };

  if (!kb) {
    return (
      <div className="flex justify-center py-16">
        <RefreshCw className="h-6 w-6 animate-spin text-volt" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-chat">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-white">AI Assistant Knowledge</h2>
          <p className="mt-1 text-sm text-slate-400">
            Update what the BNM AI Assistant knows — changes apply to new answers immediately. Core company facts
            and safety rules are protected and cannot be edited here.
          </p>
        </div>
        <button type="button" onClick={load} data-testid="admin-chat-refresh"
          className="inline-flex items-center gap-2 rounded-md border border-white/15 px-3.5 py-2 text-xs font-semibold text-slate-300 hover:text-white transition-colors">
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ListEditor title="Announcements" hint="Time-sensitive news the assistant should mention when relevant."
          items={kb.announcements} testid="kb-announcements"
          onChange={(v) => setKb({ ...kb, announcements: v })} />
        <ListEditor title="Product Notes" hint="New products, availability updates, pricing notes."
          items={kb.product_notes} testid="kb-products"
          onChange={(v) => setKb({ ...kb, product_notes: v })} />
      </div>

      <div className={cardCls}>
        <h3 className="font-display text-sm font-bold text-white">FAQs</h3>
        <p className="mt-1 text-xs text-slate-400">Question/answer pairs the assistant will use verbatim.</p>
        <div className="mt-3 space-y-3">
          {kb.faqs.map((f, i) => (
            <div key={i} className="rounded-md border border-white/10 p-3 space-y-2">
              <div className="flex gap-2">
                <input value={f.q} placeholder="Question"
                  onChange={(e) => setKb({ ...kb, faqs: kb.faqs.map((x, j) => (j === i ? { ...x, q: e.target.value } : x)) })}
                  className={fieldCls} data-testid={`kb-faq-q-${i}`} />
                <button type="button" onClick={() => setKb({ ...kb, faqs: kb.faqs.filter((_, j) => j !== i) })}
                  data-testid={`kb-faq-remove-${i}`} aria-label="Remove FAQ"
                  className="rounded-md border border-white/15 p-2 text-slate-400 hover:text-red-400 hover:border-red-400/40 transition-colors">
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              <textarea value={f.a} placeholder="Answer" rows={2}
                onChange={(e) => setKb({ ...kb, faqs: kb.faqs.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)) })}
                className={fieldCls} data-testid={`kb-faq-a-${i}`} />
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setKb({ ...kb, faqs: [...kb.faqs, { q: "", a: "" }] })} data-testid="kb-faq-add"
          className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-2 text-xs font-semibold text-slate-300 hover:text-volt hover:border-volt/40 transition-colors">
          <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add FAQ
        </button>
      </div>

      <div className={cardCls}>
        <h3 className="font-display text-sm font-bold text-white">Extra Instructions</h3>
        <p className="mt-1 text-xs text-slate-400">Free-form guidance for the assistant (e.g. "mention the new pilot programme").</p>
        <textarea value={kb.extra_instructions} rows={3}
          onChange={(e) => setKb({ ...kb, extra_instructions: e.target.value })}
          className={`${fieldCls} mt-3`} data-testid="kb-extra" />
      </div>

      <button type="button" onClick={save} disabled={saving} data-testid="kb-save"
        className="inline-flex items-center gap-2 rounded-md bg-volt px-5 py-3 text-sm font-bold text-obsidian hover:bg-emerald-300 transition-colors disabled:opacity-50">
        <Save className="h-4 w-4" aria-hidden="true" /> {saving ? "Saving…" : "Save AI Knowledge"}
      </button>

      <div className={cardCls}>
        <h3 className="font-display text-sm font-bold text-white flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-volt" aria-hidden="true" /> Recent Conversations
        </h3>
        <p className="mt-1 text-xs text-slate-400">Review what visitors ask the assistant — a source of leads.</p>
        <div className="mt-4 space-y-2">
          {sessions.length === 0 && <p className="text-sm text-slate-500" data-testid="chat-sessions-empty">No conversations yet.</p>}
          {sessions.map((s) => (
            <div key={s.session_id} className="rounded-md border border-white/10">
              <button type="button" onClick={() => viewSession(s.session_id)}
                data-testid={`chat-session-${s.session_id.slice(0, 8)}`}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-200">{s.last_message || "—"}</p>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    {s.updated_at ? new Date(s.updated_at).toLocaleString() : ""} · {s.session_id.slice(0, 8)}…
                  </p>
                </div>
                {openSession === s.session_id
                  ? <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                  : <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />}
              </button>
              {openSession === s.session_id && transcript && (
                <div className="border-t border-white/10 px-4 py-3 space-y-2 max-h-80 overflow-y-auto" data-testid="chat-transcript">
                  {(transcript.messages || []).map((m, i) => (
                    <p key={i} className={`text-xs leading-relaxed ${m.role === "user" ? "text-volt" : "text-slate-300"}`}>
                      <span className="font-bold">{m.role === "user" ? "Visitor" : "BNM AI"}: </span>
                      {m.content}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
